import { useEffect } from 'react';

interface AnimeItem {
  title: string;
  description: string;
  thumbnail: string;
  duration: string;
  category: string;
  views: string;
  rating: number;
  tags: string[];
  watchUrl: string;
}

interface AnimeDetailModalProps {
  anime: AnimeItem | null;
  onClose: () => void;
}

const categoryLabel: Record<string, string> = {
  educational: '教育',
  advanced: '上級',
  'daily-life': '日常',
};

const categoryColor: Record<string, string> = {
  educational: 'bg-amber-400 text-white',
  advanced: 'bg-orange-500 text-white',
  'daily-life': 'bg-teal-400 text-white',
};

const animeDetails: Record<string, {
  synopsis: string;
  highlights: string[];
  targetAudience: string;
  relatedTopics: string[];
  episodes: { num: number; title: string; duration: string }[];
}> = {
  '薬剤師のお仕事アニメ': {
    synopsis: '薬剤師の一日を丁寧に描いたアニメシリーズ。処方箋の受付から調剤、服薬指導、在庫管理まで、薬局業務の全体像をわかりやすく紹介します。薬剤師を目指す学生から、現役薬剤師まで幅広く楽しめる内容です。',
    highlights: ['調剤業務の流れを視覚的に解説', '服薬指導のポイントをわかりやすく紹介', '薬局スタッフのチームワークを描写', '患者さんとのコミュニケーション術'],
    targetAudience: '薬学生・新人薬剤師・薬局に興味がある方',
    relatedTopics: ['調剤薬局', '服薬指導', '薬剤師国家試験'],
    episodes: [
      { num: 1, title: '薬局の朝〜処方箋受付から始まる一日', duration: '5分' },
      { num: 2, title: '調剤室の秘密〜薬を正確に揃える技術', duration: '5分' },
      { num: 3, title: '服薬指導の極意〜患者さんに伝わる説明', duration: '5分' },
    ],
  },
  '薬の冒険物語': {
    synopsis: '薬が体内に入ってから効果を発揮するまでの旅をアニメで描いた人気シリーズ。吸収・分布・代謝・排泄（ADME）の概念をキャラクターを使って楽しく解説します。薬物動態の基礎を直感的に理解できます。',
    highlights: ['薬物動態（ADME）をキャラクターで表現', '肝臓・腎臓の役割をわかりやすく解説', '薬の相互作用を視覚的に表現', '半減期の概念をアニメで説明'],
    targetAudience: '薬学生・医療従事者・薬に興味がある方',
    relatedTopics: ['薬物動態', '薬理学', '薬の飲み合わせ'],
    episodes: [
      { num: 1, title: '薬の旅立ち〜口から体内へ', duration: '8分' },
      { num: 2, title: '血液の大河を渡る〜分布の冒険', duration: '8分' },
      { num: 3, title: '肝臓の関所〜代謝の謎を解け', duration: '8分' },
    ],
  },
  '薬局の一日': {
    synopsis: '朝の開店準備から閉店後の業務まで、薬局の一日を追ったドキュメンタリー風アニメ。患者さんとの関わりや、スタッフ間の連携、在庫管理の重要性など、薬局運営の実態をリアルに描きます。',
    highlights: ['開店準備から閉店まで時系列で解説', '患者対応の実例を多数紹介', '在庫管理・発注業務の流れ', '薬局スタッフの役割分担'],
    targetAudience: '薬局就職希望者・薬学生・薬局経営者',
    relatedTopics: ['薬局運営', '患者対応', '在庫管理'],
    episodes: [
      { num: 1, title: '開店準備〜薬局の朝は早い', duration: '6分' },
      { num: 2, title: '繁忙期の乗り越え方〜チームワークの力', duration: '6分' },
      { num: 3, title: '閉店後の業務〜見えない努力', duration: '6分' },
    ],
  },
  'お薬の正しい飲み方': {
    synopsis: '薬の正しい服用方法を丁寧に解説するアニメ。食前・食後・食間の違い、水での服用の重要性、飲み忘れた時の対処法など、患者さんが知っておくべき基本知識を網羅しています。',
    highlights: ['食前・食後・食間の正しい理解', '水以外での服用リスクを解説', '飲み忘れ時の正しい対処法', '保管方法と使用期限の注意点'],
    targetAudience: '一般患者・介護者・薬剤師（患者指導用）',
    relatedTopics: ['服薬コンプライアンス', '薬の保管', '副作用'],
    episodes: [
      { num: 1, title: '食前・食後・食間〜タイミングの大切さ', duration: '4分' },
      { num: 2, title: '水で飲む理由〜薬と飲み物の関係', duration: '4分' },
      { num: 3, title: '飲み忘れたら？〜正しい対処法', duration: '4分' },
    ],
  },
  '在宅医療と薬剤師': {
    synopsis: '高齢化社会が進む中、在宅医療における薬剤師の重要な役割をアニメで解説。訪問薬剤師の業務内容、多職種連携の実践例、患者・家族へのサポート方法など、在宅医療の現場をリアルに描きます。',
    highlights: ['訪問薬剤師の一日を密着取材風に描写', '医師・看護師・ケアマネとの連携事例', '認知症患者への服薬管理サポート', '家族への薬の説明・指導方法'],
    targetAudience: '在宅医療に興味がある薬剤師・医療従事者',
    relatedTopics: ['訪問薬剤師', '多職種連携', '認知症ケア'],
    episodes: [
      { num: 1, title: '初めての訪問〜患者さんの自宅へ', duration: '10分' },
      { num: 2, title: 'チームで支える〜多職種カンファレンス', duration: '10分' },
      { num: 3, title: '家族との絆〜介護者へのサポート', duration: '10分' },
    ],
  },
  'ジェネリック医薬品の真実': {
    synopsis: 'ジェネリック医薬品（後発医薬品）の仕組みと安全性を科学的に解説するアニメ。先発品との違い、生物学的同等性試験の意味、品質管理の実態など、患者さんの疑問に答える内容です。',
    highlights: ['先発品とジェネリックの違いを図解', '生物学的同等性試験をわかりやすく説明', '品質管理・製造工程の透明性', 'コスト削減効果と医療費への影響'],
    targetAudience: '一般患者・薬剤師・医療従事者',
    relatedTopics: ['後発医薬品', '医療費削減', '薬の品質'],
    episodes: [
      { num: 1, title: 'ジェネリックって何？〜基礎から学ぼう', duration: '7分' },
      { num: 2, title: '同等性の証明〜試験の仕組み', duration: '7分' },
      { num: 3, title: '安心して使うために〜品質管理の現場', duration: '7分' },
    ],
  },
  '漢方薬入門アニメ': {
    synopsis: '漢方医学の基礎から実践まで学べるアニメシリーズ。「証」の考え方、陰陽・気血水の概念、代表的な漢方処方の特徴など、東洋医学の世界を初心者にもわかりやすく解説します。',
    highlights: ['「証」の概念をキャラクターで表現', '気・血・水のバランスを視覚化', '代表的な漢方処方10種を紹介', '西洋医学との違いと補完関係'],
    targetAudience: '漢方に興味がある薬剤師・医療従事者・一般の方',
    relatedTopics: ['東洋医学', '証', '気血水'],
    episodes: [
      { num: 1, title: '漢方の世界へようこそ〜基本の考え方', duration: '12分' },
      { num: 2, title: '証を知る〜あなたの体質は？', duration: '12分' },
      { num: 3, title: '代表処方を学ぶ〜葛根湯から始めよう', duration: '12分' },
    ],
  },
  '薬局DX最前線': {
    synopsis: '急速に進む薬局のデジタルトランスフォーメーションをアニメで解説。電子処方箋の仕組み、オンライン服薬指導の実践、AI・ロボットの活用事例など、未来の薬局の姿を描きます。',
    highlights: ['電子処方箋の仕組みと普及状況', 'オンライン服薬指導の実践方法', '調剤ロボット・AIの活用事例', 'データ活用による薬局経営改善'],
    targetAudience: '薬局経営者・薬剤師・DXに興味がある医療従事者',
    relatedTopics: ['電子処方箋', 'オンライン服薬指導', '調剤ロボット'],
    episodes: [
      { num: 1, title: '電子処方箋の時代〜紙からデジタルへ', duration: '9分' },
      { num: 2, title: 'スマホで服薬指導〜オンラインの可能性', duration: '9分' },
      { num: 3, title: 'AIが変える薬局〜未来の調剤室', duration: '9分' },
    ],
  },
};

export default function AnimeDetailModal({ anime, onClose }: AnimeDetailModalProps) {
  useEffect(() => {
    if (anime) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [anime]);

  if (!anime) return null;

  const detail = animeDetails[anime.title] ?? {
    synopsis: anime.description,
    highlights: [],
    targetAudience: '薬剤師・医療従事者',
    relatedTopics: anime.tags,
    episodes: [],
  };

  const stars = Array.from({ length: 5 }, (_, i) => i < Math.round(anime.rating));

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      {/* オーバーレイ */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      {/* モーダル本体 */}
      <div
        className="relative bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* サムネイルヘッダー */}
        <div className="relative h-64 overflow-hidden rounded-t-2xl">
          <img
            src={anime.thumbnail}
            alt={anime.title}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />

          {/* 閉じるボタン */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-9 h-9 bg-black/40 hover:bg-black/60 text-white rounded-full flex items-center justify-center transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-lg"></i>
          </button>

          {/* カテゴリバッジ */}
          <div className="absolute top-4 left-4">
            <span className={`text-xs px-3 py-1 rounded-full font-medium ${categoryColor[anime.category] ?? 'bg-gray-400 text-white'}`}>
              {categoryLabel[anime.category] ?? anime.category}
            </span>
          </div>

          {/* タイトル・メタ情報 */}
          <div className="absolute bottom-0 left-0 right-0 p-6">
            <h2 className="text-2xl font-bold text-white mb-2 drop-shadow">{anime.title}</h2>
            <div className="flex flex-wrap items-center gap-3 text-sm text-white/90">
              <span className="flex items-center gap-1">
                <i className="ri-time-line"></i>
                {anime.duration}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line"></i>
                {anime.views}回視聴
              </span>
              <span className="flex items-center gap-1">
                {stars.map((filled, i) => (
                  <i key={i} className={filled ? 'ri-star-fill text-amber-400' : 'ri-star-line text-white/60'}></i>
                ))}
                <span className="ml-1 font-medium">{anime.rating}</span>
              </span>
            </div>
          </div>
        </div>

        {/* コンテンツ本体 */}
        <div className="p-6 space-y-6">

          {/* タグ */}
          <div className="flex flex-wrap gap-2">
            {anime.tags.map((tag) => (
              <span key={tag} className="text-xs bg-amber-50 text-amber-700 px-3 py-1 rounded-full border border-amber-200 font-medium">
                #{tag}
              </span>
            ))}
          </div>

          {/* あらすじ */}
          <div>
            <h3 className="text-base font-bold text-gray-900 mb-2 flex items-center gap-2">
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-file-text-line text-amber-500"></i>
              </span>
              あらすじ
            </h3>
            <p className="text-sm text-gray-600 leading-relaxed">{detail.synopsis}</p>
          </div>

          {/* 学習ポイント */}
          {detail.highlights.length > 0 && (
            <div>
              <h3 className="text-base font-bold text-gray-900 mb-3 flex items-center gap-2">
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-lightbulb-line text-amber-500"></i>
                </span>
                学習ポイント
              </h3>
              <ul className="space-y-2">
                {detail.highlights.map((point, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0">
                      <i className="ri-checkbox-circle-fill text-amber-400"></i>
                    </span>
                    {point}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* 対象者 */}
          <div className="bg-amber-50 rounded-xl p-4 flex items-start gap-3">
            <span className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-user-heart-line text-amber-500 text-lg"></i>
            </span>
            <div>
              <p className="text-xs font-semibold text-amber-700 mb-0.5">対象者</p>
              <p className="text-sm text-gray-700">{detail.targetAudience}</p>
            </div>
          </div>

          {/* エピソード一覧 */}
          {detail.episodes.length > 0 && (
            <div>
              <h3 className="text-base font-bold text-gray-900 mb-3 flex items-center gap-2">
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-play-list-line text-amber-500"></i>
                </span>
                エピソード一覧
              </h3>
              <div className="space-y-2">
                {detail.episodes.map((ep) => (
                  <div
                    key={ep.num}
                    className="flex items-center gap-3 p-3 rounded-lg border border-gray-100 hover:border-amber-200 hover:bg-amber-50/50 transition-colors cursor-pointer group"
                  >
                    <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-amber-400 transition-colors">
                      <i className="ri-play-fill text-amber-500 group-hover:text-white text-sm transition-colors"></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-amber-600 font-medium mb-0.5">EP.{ep.num}</p>
                      <p className="text-sm text-gray-800 font-medium truncate">{ep.title}</p>
                    </div>
                    <span className="text-xs text-gray-400 whitespace-nowrap">{ep.duration}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 関連トピック */}
          <div>
            <h3 className="text-base font-bold text-gray-900 mb-2 flex items-center gap-2">
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-links-line text-amber-500"></i>
              </span>
              関連トピック
            </h3>
            <div className="flex flex-wrap gap-2">
              {detail.relatedTopics.map((topic) => (
                <span key={topic} className="text-xs bg-gray-100 text-gray-600 px-3 py-1 rounded-full hover:bg-amber-100 hover:text-amber-700 transition-colors cursor-pointer">
                  {topic}
                </span>
              ))}
            </div>
          </div>

          {/* アクションボタン */}
          <div className="flex gap-3 pt-2">
            <button className="flex-1 bg-amber-500 hover:bg-amber-600 text-white py-3 rounded-xl font-medium transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2">
              <i className="ri-play-circle-line text-lg"></i>
              今すぐ視聴する
            </button>
            <button className="w-12 h-12 border border-gray-200 hover:border-amber-300 hover:bg-amber-50 rounded-xl flex items-center justify-center transition-colors cursor-pointer flex-shrink-0">
              <i className="ri-bookmark-line text-gray-500 hover:text-amber-500"></i>
            </button>
            <button className="w-12 h-12 border border-gray-200 hover:border-amber-300 hover:bg-amber-50 rounded-xl flex items-center justify-center transition-colors cursor-pointer flex-shrink-0">
              <i className="ri-share-line text-gray-500 hover:text-amber-500"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
